import { useStore } from '@/services/layer-management-service/store'
import type { EventCallback, Events } from '@onxmaps/layer-management'

type Callback = () => void

const cleanup: Callback[] = []

const store = useStore()

/**
 * *(AI-generated)*
 * Registers event listeners for layer management service events.
 */
export const registerListeners = (events: Events) => {
  // Stylesheet Events

  events.once('initial-stylesheet', (e) =>
    store.updateInitialStylesheet(e.detail)
  )
  events.subscribe('show-layers', updateShowLayers)
  events.subscribe('hide-layers', updateHideLayers)
  events.subscribe('layer-opacities-updated', updateLayerOpacities)
  events.subscribe('layer-filters-updated', updateLayerFilters)
  events.subscribe('stylesheet-sources-updated', updateStylesheetSources)
  events.subscribe('stylesheet-update', updateStylesheet)
  events.subscribe('storage-updated', updateStorage)

  cleanup.push(
    () => events.unsubscribe('show-layers', updateShowLayers),
    () => events.unsubscribe('layer-opacities-updated', updateLayerOpacities),
    () => events.unsubscribe('layer-filters-updated', updateLayerFilters),
    () =>
      events.unsubscribe('stylesheet-sources-updated', updateStylesheetSources),
    () => events.unsubscribe('stylesheet-update', updateStylesheet),
    () => events.unsubscribe('storage-updated', updateStorage)
  )

  // Layer Management Events

  events.subscribe('user-layer-update', updateUserLayer)
  events.subscribe('user-layer-tag-update', updateUserLayerTag)
  events.subscribe('tags-to-user-layer-update', updateTagsUserLayerIds)

  cleanup.push(
    () => events.unsubscribe('user-layer-update', updateUserLayer),
    () => events.unsubscribe('user-layer-tag-update', updateUserLayerTag),
    () =>
      events.unsubscribe('tags-to-user-layer-update', updateTagsUserLayerIds)
  )
}

/**
 * *(AI-generated)*
 * Removes all registered layer management event listeners.
 */
export const cleanupListeners = () => {
  while (cleanup.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    cleanup.shift()!.call(this)
  }
}

const updateShowLayers: EventCallback<'show-layers'> = (e) => {
  store.updateShowLayers(e.detail)
}

const updateHideLayers: EventCallback<'hide-layers'> = (e) => {
  store.updateHideLayers(e.detail)
}

const updateLayerOpacities: EventCallback<'layer-opacities-updated'> = (e) => {
  store.updateLayerOpacities(e.detail)
}
const updateLayerFilters: EventCallback<'layer-filters-updated'> = (e) => {
  store.updateLayerFilters(e.detail)
}

const updateStylesheetSources: EventCallback<'stylesheet-sources-updated'> = (
  e
) => {
  store.updateStylesheetSources(e.detail)
}

const updateStylesheet: EventCallback<'stylesheet-update'> = (e) => {
  store.updateStylesheet(e.detail)
}

const updateUserLayer: EventCallback<'user-layer-update'> = (e) => {
  store.updateUserLayer(e.detail)
}

const updateUserLayerTag: EventCallback<'user-layer-tag-update'> = (e) => {
  store.updateUserLayerTag(e.detail)
}

const updateTagsUserLayerIds: EventCallback<'tags-to-user-layer-update'> = (
  e
) => {
  store.updateTagsUserLayerIds(e.detail)
}

const updateStorage: EventCallback<'storage-updated'> = (e) => {
  store.updateStorage(e.detail)
}
